/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Autocomplete,
    Alert,
    Stack,
    Backdrop,
    Chip,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";
import ImportProgress from "./components/ImportCustomerProgress";

const ROW_STATUS = {
    SAVED: "saved",
    MODIFIED: "modified",
    IMPORTED: "imported",
    ERROR: "error",
};

function TaxCodeMapping() {
    const [items, setItems] = useState([]);
    const [taxCodes, setTaxCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [error, setError] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rowStatuses, setRowStatuses] = useState({});
    const [statusFilter, setStatusFilter] = useState("all");

    // Import and Error Modal States
    const [status, setStatus] = useState("idle");
    const [data, setData] = useState(null);
    const [orphanErrors, setOrphanErrors] = useState([]);
    const [errorDetail, setErrorDetail] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [severity, setSeverity] = useState("success");
    const [alertMessage, setAlertMessage] = useState("");

    const tenantId = sessionStorage.getItem("rilletUserId");

    const showNotification = (type, msg) => {
        setSeverity(type);
        setAlertMessage(msg);
        setIsOpen(true);
    };

    const handleClose = () => setIsOpen(false);

    const safeJson = async (res) => {
        const text = await res.text();
        if (!text) return [];
        try {
            return JSON.parse(text);
        } catch {
            return [];
        }
    };

    const fetchData = async () => {
        if (!tenantId) {
            setError("Missing tenant information. Please log in again.");
            setItems([]);
            setTaxCodes([]);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const [itemsRes, taxCodesRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?tenantId=${tenantId}`, {
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` },
                }),
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/zamp?tenantId=${tenantId}`, {
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` },
                }),
            ]);

            if (itemsRes.status === 401 || taxCodesRes.status === 401) {
                handleSessionExpiredClose();
                setItems([]);
                setTaxCodes([]);
                return;
            }

            if (!itemsRes.ok || !taxCodesRes.ok) {
                const failedRes = !itemsRes.ok ? itemsRes : taxCodesRes;
                const errorBody = await safeJson(failedRes);
                throw new Error(errorBody?.message || "Something went wrong. Please try again later.");
            }

            const [itemsData, taxCodesData] = await Promise.all([safeJson(itemsRes), safeJson(taxCodesRes)]);
            setItems(itemsData || []);
            setTaxCodes(taxCodesData || []);
            setError("");

        } catch (err) {
            setError(err.message || "Error fetching data");
            setItems([]);
            setTaxCodes([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [tenantId]);

    const handleTaxCodeChange = (customerId, newEntity) => {
        setMessage({ type: "", text: "" });
        setItems(prev => prev.map(it => (it.customerId === customerId ? { ...it, zampEntity: newEntity } : it)));
        const item = items.find(it => it.customerId === customerId);
        if (item) {
            setRowStatuses(prev => ({
                ...prev,
                [item.customerId]: { status: ROW_STATUS.MODIFIED, tooltip: "Modified — not yet saved" },
            }));
        }
        setUnsavedChanges(true);
    };

    const handleSave = async () => {
        if (!tenantId) {
            setError("Missing tenant information. Please log in again.");
            return;
        }
        setSaving(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/mapping?tenantId=${tenantId}`, {
                method: "POST",
                headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}`, "Content-Type": "application/json" },
                body: JSON.stringify(items),
            });
            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) throw new Error("Failed to save mappings");

            setMessage({ type: "success", text: "Customer entity mappings saved successfully!" });
            setUnsavedChanges(false);
            setRowStatuses({});
            setStatusFilter("all");
        } catch (err) {
            setMessage({ type: "error", text: err.message || "Error saving mappings" });
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        setMessage({ type: "", text: "" });
        setRefreshing(true);
        fetchData();

        // Reset import data
        setData(null);
        setStatus("idle");

        // Reset all status trackers, errors, and unsaved changes
        setRowStatuses({});
        setOrphanErrors([]);
        setStatusFilter("all");
        setUnsavedChanges(false);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExport = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/export?tenantId=${tenantId}`, {
                method: "GET",
                headers: { Authorization: `${sessionStorage.getItem("token")}`, "ngrok-skip-browser-warning": "true" },
            });

            if (!response.ok) throw new Error("Export failed");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            const disposition = response.headers.get("Content-Disposition");
            const formattedDate = new Date().toISOString().split("T")[0];
            let fileName = `customer_entity_data_${formattedDate}.xlsx`;
            if (disposition && disposition.includes("filename=")) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            showNotification("error", "Unexpected error occurred");
        }
    };

    const handleImport = async (event) => {
        setMessage({ type: "", text: "" });
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        setStatus("loading");
        setRowStatuses({});
        setOrphanErrors([]);
        setStatusFilter("all");
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/customer/import?tenantId=${tenantId}`, {
                method: "POST",
                headers: { Authorization: `${sessionStorage.getItem("token")}`, "ngrok-skip-browser-warning": "true" },
                body: formData,
            });

            if (response.status === 401) {
                handleSessionExpiredClose();
                setStatus("idle");
                return;
            }

            if (response.status === 400) {
                const errorText = await response.text();
                showNotification("error", errorText || "Invalid file format");
                setStatus("idle");
                return;
            }

            if (!response.ok) {
                showNotification("error", "Import failed");
                setStatus("idle");
                return;
            }

            const json = await response.json();
            setData(json);

            const newStatuses = {};
            const newOrphanErrors = [];
            const previousItemMap = new Map(items.map(it => [it.customerId, it]));

            const importedMappings = json.customerMappings || [];

            if (importedMappings) {
                importedMappings.forEach(newItem => {
                    const oldItem = previousItemMap.get(newItem.customerId);
                    if (oldItem && oldItem.zampEntity !== newItem.zampEntity) {
                        newStatuses[newItem.customerId] = {
                            status: ROW_STATUS.IMPORTED,
                            tooltip: "Updated via import — not yet saved",
                        };
                    }
                });
            }

            if (json.invalidProducts?.length > 0) {
                json.invalidProducts.forEach((prod, i) => {
                    newOrphanErrors.push({
                        customerId: `orphan-prod-${i}`,
                        customerName: prod,
                        zampEntity: "",
                        _orphan: true,
                        _error: "Customer not found in the system",
                    });
                });
            }

            if (json.invalidTaxCodes?.length > 0) {
                json.invalidTaxCodes.forEach((tc, i) => {
                    newOrphanErrors.push({
                        customerId: `orphan-tc-${i}`,
                        customerName: "",
                        zampEntity: tc,
                        _orphan: true,
                        _error: `Invalid Zamp Entity: "${tc}"`,
                    });
                });
            }

            setRowStatuses(newStatuses);
            setOrphanErrors(newOrphanErrors);

            if (json.summary?.updatedCount) {
                setItems(importedMappings || []);
                setPage(0);
                setUnsavedChanges(true);
            }
            setStatus("completed");
        } catch (err) {
            setData(null);
            setStatus("idle");
            showNotification("error", "Unexpected error occurred");
        } finally {
            event.target.value = null;
        }
    };

    const handleDismiss = () => {
        setData(null);
        setStatus("idle");
        setOrphanErrors([]);
        setRowStatuses(prev => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                if (v.status !== ROW_STATUS.ERROR) next[k] = v;
            });
            return next;
        });
        setStatusFilter("all");
    };

    const getRowStatusIcon = (item) => {
        const info = rowStatuses[item.customerId];
        if (!info || info.status === ROW_STATUS.SAVED) {
            return (
                <Tooltip title="Saved" placement="left">
                    <CloudDoneOutlinedIcon sx={{ fontSize: 16, color: "grey.400" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.MODIFIED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <EditIcon sx={{ fontSize: 16, color: "warning.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.IMPORTED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.ERROR) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <ErrorOutlineIcon
                        sx={{ fontSize: 16, color: "error.main", cursor: "pointer" }}
                        onClick={() => setErrorDetail(info)}
                    />
                </Tooltip>
            );
        }
        return null;
    };

    const hasActiveStatuses = Object.keys(rowStatuses).length > 0;

    const statusCounts = (() => {
        if (!hasActiveStatuses) return {};
        const counts = { [ROW_STATUS.SAVED]: 0, [ROW_STATUS.MODIFIED]: 0, [ROW_STATUS.IMPORTED]: 0, [ROW_STATUS.ERROR]: 0 };
        items.forEach(it => {
            const s = rowStatuses[it.customerId]?.status || ROW_STATUS.SAVED;
            counts[s]++;
        });
        return counts;
    })();

    const filteredItems = (() => {
        const search = filterText.toLowerCase();
        const base = items.filter(it => {
            const matchesText = it.customerName.toLowerCase().includes(search) ||
                (it.zampEntity || "").toLowerCase().includes(search);
            if (!matchesText) return false;
            if (statusFilter === "all") return true;
            const s = rowStatuses[it.customerId]?.status || ROW_STATUS.SAVED;
            return s === statusFilter;
        });
        if (statusFilter === ROW_STATUS.ERROR && orphanErrors.length > 0) {
            const filtered = orphanErrors.filter(o =>
                o.customerName.toLowerCase().includes(search) ||
                (o.zampEntity || "").toLowerCase().includes(search)
            );
            return [...base, ...filtered];
        }
        return base;
    })();

    if (loading) return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 64px)" }}>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading mappings...
            </Typography>
        </Box>
    );

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            {/* Page Heading */}
            <Typography className="zamp-page-title" sx={{ mb: 2, textAlign: "center" }}>
                Customer Entity Mapping Utility
            </Typography>

            {/* Table context setup wrapper */}
            <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 1050, width: "100%", mx: "auto", mt: 1 }}>
                <Stack spacing={2} direction="column" sx={{ width: "100%", mt: 2 }}>

                    {/* Filter, Import, Export & Refresh Controls */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
                        <TextField
                            size="small"
                            label="Filter by customer name or zamp entity"
                            placeholder="Start typing to filter by customer name or zamp entity"
                            value={filterText}
                            onChange={e => {
                                setFilterText(e.target.value);
                                setPage(0);
                                if (message.text) setMessage({ type: "", text: "" });
                            }}
                            sx={{ flexBasis: "50%" }}
                        />
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                            <input
                                type="file"
                                accept=".xlsx"
                                style={{ display: "none" }}
                                id="import-file"
                                onChange={handleImport}
                            />
                            <label htmlFor="import-file">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="span"
                                    startIcon={<UploadIcon />}
                                >
                                    Import
                                </Button>
                            </label>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={handleExport}
                            >
                                Export
                            </Button>
                        </Box>
                        <Tooltip title="Refresh Rillet customers">
                            <IconButton
                                type="button"
                                className="zamp-icon-action"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <RefreshIcon sx={{ fontSize: 24, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Import Status Tracking Blocks */}
                    {status === "loading" && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Loading import progress...</Typography>
                        </Box>
                    )}

                    {status === "completed" && (
                        <ImportProgress data={data} onDismiss={handleDismiss} />
                    )}

                    {/* Status Filter Chips */}
                    {hasActiveStatuses && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
                            <Chip
                                label={`All (${items.length})`}
                                size="small"
                                variant={statusFilter === "all" ? "filled" : "outlined"}
                                onClick={() => { setStatusFilter("all"); setPage(0); }}
                            />
                            {(statusCounts[ROW_STATUS.IMPORTED] || 0) > 0 && (
                                <Chip
                                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                    label={statusCounts[ROW_STATUS.IMPORTED]}
                                    size="small"
                                    variant={statusFilter === ROW_STATUS.IMPORTED ? "filled" : "outlined"}
                                    color="success"
                                    onClick={() => { setStatusFilter(ROW_STATUS.IMPORTED); setPage(0); }}
                                />
                            )}
                            {(statusCounts[ROW_STATUS.MODIFIED] || 0) > 0 && (
                                <Chip
                                    icon={<EditIcon sx={{ fontSize: 14 }} />}
                                    label={statusCounts[ROW_STATUS.MODIFIED]}
                                    size="small"
                                    variant={statusFilter === ROW_STATUS.MODIFIED ? "filled" : "outlined"}
                                    color="warning"
                                    onClick={() => { setStatusFilter(ROW_STATUS.MODIFIED); setPage(0); }}
                                />
                            )}
                            {((statusCounts[ROW_STATUS.ERROR] || 0) > 0 || orphanErrors.length > 0) && (
                                <Chip
                                    icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                                    label={(statusCounts[ROW_STATUS.ERROR] || 0) + orphanErrors.length}
                                    size="small"
                                    variant={statusFilter === ROW_STATUS.ERROR ? "filled" : "outlined"}
                                    color="error"
                                    onClick={() => { setStatusFilter(ROW_STATUS.ERROR); setPage(0); }}
                                />
                            )}
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
                    )}

                    {/* Core Mapping Table */}
                    <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "50%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Customer Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "50%" }}>Zamp Entity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                                    const isOrphan = !!item._orphan;
                                    const info = isOrphan ? null : rowStatuses[item.customerId];
                                    const s = isOrphan ? ROW_STATUS.ERROR : info?.status;
                                    const rowBg = s === ROW_STATUS.IMPORTED
                                        ? (index % 2 === 0 ? "#f6faf3" : "#eef5ea")
                                        : s === ROW_STATUS.ERROR
                                            ? (index % 2 === 0 ? "#fef7f7" : "#fdf0f0")
                                            : s === ROW_STATUS.MODIFIED
                                                ? (index % 2 === 0 ? "#fffbf0" : "#fff7e6")
                                                : (index % 2 === 0 ? "white" : "grey.100");

                                    return (
                                        <TableRow key={item.customerId} sx={{ backgroundColor: rowBg }}>
                                            <TableCell sx={{
                                                borderRight: "1px solid rgba(224, 224, 224, 1)",
                                                maxWidth: 300,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}>
                                                <Tooltip
                                                    title={item.customerName}
                                                    placement="bottom"
                                                    slotProps={{
                                                        popper: { modifiers: [{ name: "offset", options: { offset: [0, 8] } }] },
                                                        tooltip: { sx: { color: "white", fontSize: "0.8rem", borderRadius: 1, px: 1.5, py: 0.5 } }
                                                    }}
                                                >
                                                    <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {item.customerName}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        {isOrphan ? (
                                                            <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                                {item.zampEntity || "-"}
                                                            </Typography>
                                                        ) : (
                                                            <Autocomplete
                                                                options={taxCodes}
                                                                getOptionLabel={(option) => (typeof option === "string" ? option : option.code)}
                                                                renderOption={(props, option) => <li {...props}>{option.code}</li>}
                                                                value={taxCodes.find(tc => tc.code === item.zampEntity) || item.zampEntity || ""}
                                                                onChange={(e, newValue) => {
                                                                    handleTaxCodeChange(item.customerId, newValue ? (typeof newValue === "string" ? newValue : newValue.code) : "");
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} size="small" placeholder="Start typing to select zamp entity" />
                                                                )}
                                                                fullWidth
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center" }}>
                                                        {isOrphan ? (
                                                            <Tooltip title={item._error} placement="left">
                                                                <ErrorOutlineIcon
                                                                    sx={{ fontSize: 14, color: "error.main", cursor: "pointer" }}
                                                                    onClick={() => setErrorDetail({
                                                                        error: item._error,
                                                                        rowData: { itemCode: item.customerId, itemName: item.customerName, taxCode: item.zampEntity },
                                                                    })}
                                                                />
                                                            </Tooltip>
                                                        ) : getRowStatusIcon(item)}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredItems.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />

                    {/* Message Reporting and Save Control */}
                    <Stack spacing={1} sx={{ mt: 2, width: "100%", maxWidth: 800 }}>
                        {message.type === "error" && (
                            <Typography color="error" sx={{ fontSize: 14 }}>
                                {message.text}
                            </Typography>
                        )}

                        {message.type === "success" && (
                            <Typography color="success.main" sx={{ fontSize: 14 }}>
                                {message.text}
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving || !unsavedChanges}
                            sx={{ width: 250 }}
                            startIcon={saving ? <CircularProgress size={18} thickness={5} sx={{ color: "white" }} /> : null}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Error Detail Dialog Overlay */}
            <Dialog open={!!errorDetail} onClose={() => setErrorDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>Error Details</DialogTitle>
                <DialogContent dividers>
                    {errorDetail?.rowData && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Customer ID</Typography>
                                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.itemCode}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                                <Typography variant="body1">{errorDetail.rowData.itemName || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Zamp Entity</Typography>
                                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.taxCode || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Error</Typography>
                                <Typography variant="body1" color="error.main">{errorDetail.error}</Typography>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Backdrop Spinner Loader overlay during direct save sequence */}
            <Backdrop open={saving} className="zamp-backdrop-loader" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <CircularProgress size={48} thickness={4} color="inherit" />
                <Typography variant="body1">Saving mappings...</Typography>
            </Backdrop>

            {/* Global Context Toast Notifications */}
            <Snackbar open={isOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TaxCodeMapping;
